import React, { createContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import {
  getAllStudents,
  getStudentById,
  createStudent,
  updateStudent,
  deleteStudent,
  getStudentGradesWithDetails
} from '../services/studentService';
import Notification from '../components/Common/Notification';

export const StudentContext = createContext();

export const StudentProvider = ({ children }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const [notification, setNotification] = useState({ message: '', isError: false });

  const fetchAuthHeaders = () => {
    const storedUser = localStorage.getItem('user');
    const token = user?.token || (storedUser ? JSON.parse(storedUser).token : '');

    if (!token) {
      console.error('User or token not available');
      return {};
    }

    return {
      Authorization: `Bearer ${token}`,
    };
  };

  const showNotification = (message, isError) => {
    setNotification({ message, isError });
  };

  const closeNotification = () => {
    setNotification({ message: '', isError: false });
  };

  const fetchAllStudents = async () => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getAllStudents(authHeaders);
      setStudents(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error fetching students.';
      showNotification(errorMessage, true);
    }
  };

  const fetchStudentById = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const data = await getStudentById(id, authHeaders);
      setSelectedStudent(data);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching student with ID ${id}.`;
      showNotification(errorMessage, true);
    }
  };

  const addStudent = async (studentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const newStudent = await createStudent(studentData, authHeaders);
      setStudents(prevStudents => [...prevStudents, newStudent]);
      showNotification('Student added successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error adding student.';
      showNotification(errorMessage, true);
    }
  };

  const editStudent = async (id, studentData) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const updatedStudent = await updateStudent(id, studentData, authHeaders);
      setStudents(prevStudents =>
        prevStudents.map(student => (student.id === id ? updatedStudent : student))
      );
      showNotification('Student updated successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error updating student.';
      showNotification(errorMessage, true);
    }
  };

  const removeStudent = async (id) => {
    try {
      const authHeaders = fetchAuthHeaders();
      await deleteStudent(id, authHeaders);
      setStudents(prevStudents => prevStudents.filter(student => student.id !== id));
      showNotification('Student removed successfully!', false);
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || 'Error removing student.';
      showNotification(errorMessage, true);
    }
  };

  const getStudentGrade = async (studentId) => {
    try {
      const authHeaders = fetchAuthHeaders();
      const grades = await getStudentGradesWithDetails(studentId, authHeaders);
      return grades;
    } catch (error) {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || `Error fetching grades for student with ID ${studentId}.`;
      showNotification(errorMessage, true);
      return null;
    }
  };

  // useEffect(() => {
  //   fetchAllStudents();
  // }, []);


  useEffect(() => {
    if (!isAuthenticated && !['/', '/login'].includes(window.location.pathname)) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);


  return (
    <StudentContext.Provider
      value={{
        students,
        selectedStudent,
        fetchAllStudents,
        fetchStudentById,
        addStudent,
        editStudent,
        removeStudent,
        getStudentGrade,
      }}
    >
      {children}
      {notification.message && (
        <Notification
          message={notification.message}
          isError={notification.isError}
          onClose={closeNotification}
        />
      )}
    </StudentContext.Provider>
  );
};
